import { useDispatch } from "react-redux";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { PlainTooltip } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import signalEventsService from "app/pages/sales-intelligence/modules/company-analysis/services/signals/signalEventsService";
import type {
    SignalEventsFilterConfigItemType,
    SignalEventType,
} from "app/pages/sales-intelligence/modules/company-analysis/services/signals/types";
import companyAnalysisTrackingService from "app/pages/sales-intelligence/modules/company-analysis/services/tracking/companyAnalysisTrackingService";
import { translate } from "app/filters/i18nFilter";
import SignalType from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalType";
import {
    isDropdownOpenAction,
    setPreselectedAccountOverviewSignalType,
} from "app/pages/sales-intelligence/sub-modules/signals/store/action-creators";

import FilterButton, { FilterButtonVariant } from "../FIlterButton/FilterButton";

type FilterButtonProps = {
    config: SignalEventsFilterConfigItemType;
    onClick: (value: SignalEventType) => void;
    filterValue: SignalEventType;
};

const SignalEventFilterButton = (props: FilterButtonProps) => {
    const dispatch = useDispatch();
    const { filterValue, onClick, config } = props;
    const { value, label, count, tooltip, active } = config;

    const isDisabled = active && count === 0;

    const getButtonVariant = () => {
        if (value === filterValue) {
            return FilterButtonVariant.selected;
        }
        if (isDisabled) {
            return FilterButtonVariant.disabled;
        }
        if (!active) {
            return FilterButtonVariant.dashed;
        }
    };

    const handleButtonClick = () => {
        if (active) {
            onClick(value);
            companyAnalysisTrackingService.signalEvents.trackClickOnActiveSignalsFilter(value);
        } else {
            companyAnalysisTrackingService.signalEvents.trackClickOnInactiveSignalsFilter(value);
            const preselectedType = signalEventsService.isTypeFilter(value)
                ? (value as SignalType)
                : signalEventsService.isSubTypeFilter(value)
                  ? SignalType.News
                  : null;
            if (preselectedType) {
                dispatch(setPreselectedAccountOverviewSignalType(preselectedType));
            }
            dispatch(isDropdownOpenAction(true));
        }
    };

    const renderButton = () => (
        <Box position="relative">
            <FilterButton
                onClick={handleButtonClick}
                disabled={isDisabled}
                variant={getButtonVariant()}
            >
                {active ? (
                    `${label} (${count})`
                ) : (
                    <>
                        <SWReactIcons
                            iconName="add"
                            size="xs"
                            iconColor={colorsPalettes.blue[400]}
                        />
                        {label}
                    </>
                )}
            </FilterButton>
        </Box>
    );

    if (isDisabled) {
        return (
            <PlainTooltip
                tooltipContent={translate("si.account-overview.signals.filter.no-signals.tooltip")}
                maxWidth={230}
            >
                {renderButton()}
            </PlainTooltip>
        );
    }

    if (!active && tooltip) {
        return (
            <PlainTooltip
                tooltipContent={tooltip}
                maxWidth={230}
            >
                {renderButton()}
            </PlainTooltip>
        );
    }

    return renderButton();
};

export default SignalEventFilterButton;
